import { useEffect, useMemo, useRef, useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { runThirdParty } from '../../helpers/thirdparty'
import {
  pushFormSubmit,
  useFormStartWhenVisible,
  createD365FormLoadHandler,
  registerD365FormLoadListeners,
} from '../../helpers/formAnalytics'
import { registerPrefillHandler, applyPrefillToForm } from './d365FormPrefill'
import { captureD365FormFile, uploadD365FormFile } from '../../helpers/d365FormFileUpload'
import FormSubmitOverlay from '../helpers/FormSubmitOverlay'

/**
 * Renders one of three D365 Marketing embedded registration forms based on URL query params:
 *
 *   - no query params           → check form (user enters email; D365 determines provider vs generic
 *                                  and emails a link back to this page with query params set)
 *   - ?type=provider&contactId… → staff registration form (pre-populated from query params)
 *   - ?type=new&contactId…      → membership application form (pre-populated from query params)
 *
 * On successful submission, D365 fires d365mkt-afterformsubmit events in the DOM.
 * Provider and new-user forms POST to /umbraco/surface/memberregistration/callback which
 * provisions the login, writes registration-submitted to D365, and returns routing.
 */
function RegistrationForm(props) {
  const {
    formApiUrl,
    cachedFormBaseUrl,
    checkFormId,
    providerFormId,
    newUserFormId,
    allowedOrigin,
    callbackUrl,
    submitCallbackUrl,
    prefillUrl,
    successMessage,
    redirectOnSuccessUrl,
    isSectionVisible = true,
  } = props
  const containerRef = useRef(null)
  const interactionCleanupRef = useRef(null)
  const submittedEmailRef = useRef(null)
  const capturedFileRef = useRef(null)
  const dlFormType = 'registration'

  const [outcome, setOutcome] = useState(null)
  // Details resolved server-side from the emailed token (see the /prefill call below).
  const [tokenPrefill, setTokenPrefill] = useState(null)
  // Covers the form the instant D365 accepts the submission, keeping the member on the
  // page while the async check/callback runs before the outcome panel or redirect lands.
  const [submitting, setSubmitting] = useState(false)

  const queryParams = useMemo(() => {
    if (typeof window === 'undefined') return {}
    const params = new URLSearchParams(window.location.search)
    const result = {}
    for (const [key, value] of params.entries()) {
      result[key] = value
    }
    return result
  }, [])

  const { formId, formType, prefill } = useMemo(() => {
    const type = (queryParams.type || '').toLowerCase()
    if (type === 'provider' && providerFormId) {
      return { formId: providerFormId, formType: 'provider', prefill: queryParams }
    }
    if (type === 'new' && newUserFormId) {
      return { formId: newUserFormId, formType: 'new', prefill: queryParams }
    }
    return { formId: checkFormId, formType: 'check', prefill: null }
  }, [queryParams, checkFormId, providerFormId, newUserFormId])

  // Query-string values first, then anything the token resolved server-side — the token wins, since a
  // URL parameter can be edited by whoever holds the link while the signed token cannot.
  const prefillContext = useMemo(() => {
    const base = buildRegistrationPrefillContext(prefill)
    const fromToken = nonEmptyValues(tokenPrefill)
    if (!fromToken) return base
    return { ...(base || {}), ...fromToken }
  }, [prefill, tokenPrefill])
  const cachedFormUrl = formId && cachedFormBaseUrl ? `${cachedFormBaseUrl}/${formId}` : undefined

  const runCheck = useCallback(
    (email, contactId) => {
      if (!email) return

      setSubmitting(true)
      pushFormSubmit(dlFormType)

      fetch(callbackUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ email, contactId: contactId || '00000000-0000-0000-0000-000000000000' }),
      })
        .then((response) => {
          if (!response.ok) throw new Error('Check request failed: ' + response.status)
          return response.json()
        })
        .then((result) => {
          if (result.message) {
            setSubmitting(false)
            setOutcome({ status: result.status, message: result.message, redirectUrl: result.redirectUrl })
            return
          }
          const target = redirectOnSuccessUrl || result.redirectUrl
          if (successMessage) {
            setSubmitting(false)
            setOutcome({ status: 'success', html: successMessage, redirectUrl: target })
            return
          }
          if (target) {
            // Overlay stays up through the redirect (page unloads).
            window.location.href = target
            return
          }
          setSubmitting(false)
        })
        .catch((err) => {
          console.error('[RegistrationForm] Error during member check:', err)
          setSubmitting(false)
        })
    },
    [callbackUrl, successMessage, redirectOnSuccessUrl],
  )

  const runRegistrationCallback = useCallback(
    async (email) => {
      if (!email || !submitCallbackUrl) return
      setSubmitting(true)
      pushFormSubmit(dlFormType)

      const contactId = resolveContactId(prefillContext, queryParams)
      try {
        const response = await fetch(submitCallbackUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ email, contactId, signInForFileUpload: formType === 'new' }),
        })
        if (!response.ok) throw new Error('registration callback failed: ' + response.status)
        const result = await response.json()

        // New-applicant forms include the same Upload File field as renewals. D365 does not push the
        // file to CRM — callback provisions the login and signs the member in so the shared upload
        // endpoint can attach it to their Membership Request.
        if (formType === 'new') {
          await uploadD365FormFile(capturedFileRef.current, 'RegistrationForm')
        }

        setSubmitting(false)
        // No CTA after a form submission: every post-submit message tells the applicant to check
        // their email for a temporary password, and the target page needs a login they do not have
        // yet. Carrying redirectUrl here rendered a dead "Continue" (RegistrationCompleteUrl has no
        // page) and duplicated the link already sent by email. Check-form outcomes keep their CTA.
        setOutcome({ status: result.status, message: result.message })
      } catch (err) {
        console.error('[RegistrationForm] Error on registration callback:', err)
        setSubmitting(false)
      }
    },
    [queryParams, prefillContext, formType, submitCallbackUrl],
  )

  const handleMessage = useCallback(
    (event) => {
      if (allowedOrigin && event.origin !== allowedOrigin) return
      const data = event.data
      if (!data || !data.email) return
      runCheck(data.email, data.contactId)
    },
    [allowedOrigin, runCheck],
  )

  useEffect(() => {
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [handleMessage])

  useEffect(() => {
    const captureSubmitFields = (event) => {
      const formEl = event?.detail?.formElement ?? event?.target ?? containerRef.current
      const email = readSubmittedEmail(formEl)
      if (email) submittedEmailRef.current = email
      if (formType === 'new') {
        const file = captureD365FormFile(formEl)
        if (file) capturedFileRef.current = file
      }
    }

    const onAfterSubmit = async (event) => {
      if (!isSubmissionSuccessful(event)) return
      const email = submittedEmailRef.current || readSubmittedEmail(containerRef.current)
      if (!email) return
      if (formType === 'check') runCheck(email)
      else await runRegistrationCallback(email)
    }

    const api = typeof window !== 'undefined' ? window.d365mktforms : null
    const targets = [api, typeof document !== 'undefined' ? document : null].filter(Boolean)
    targets.forEach((t) => {
      t.addEventListener?.('d365mkt-formsubmit', captureSubmitFields)
      t.addEventListener?.('d365mkt-afterformsubmit', onAfterSubmit)
    })

    return () => {
      targets.forEach((t) => {
        t.removeEventListener?.('d365mkt-formsubmit', captureSubmitFields)
        t.removeEventListener?.('d365mkt-afterformsubmit', onAfterSubmit)
      })
    }
  }, [formType, runCheck, runRegistrationCallback])

  useEffect(() => {
    if (formType !== 'provider' || !queryParams.token || !prefillUrl) return

    const controller = new AbortController()
    fetch(`${prefillUrl}?token=${encodeURIComponent(queryParams.token)}`, {
      credentials: 'same-origin',
      signal: controller.signal,
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (data?.email) {
          // The endpoint returns the member's D365 details, not just the email — name, title, mobile,
          // position, state and organisation where D365 knows them.
          setTokenPrefill(data)
          submittedEmailRef.current = data.email
        }
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          console.error('[RegistrationForm] Failed to resolve registration prefill token:', err)
        }
      })

    return () => controller.abort()
  }, [formType, queryParams.token, prefillUrl])

  useFormStartWhenVisible(dlFormType, { ready: !!formId, isSectionVisible })

  useEffect(() => {
    const handleFormLoad = createD365FormLoadHandler(containerRef, interactionCleanupRef)
    const unregister = registerD365FormLoadListeners(handleFormLoad)

    return () => {
      unregister()
      interactionCleanupRef.current?.()
      interactionCleanupRef.current = null
    }
  }, [formId])

  useEffect(() => {
    if (formType === 'check' || !prefillContext) return

    registerPrefillHandler(prefillContext)

    let attempts = 0
    const retryId = window.setInterval(() => {
      attempts += 1
      registerPrefillHandler(prefillContext)
      containerRef.current?.querySelectorAll('form').forEach((form) => {
        applyPrefillToForm(form, prefillContext)
      })
      if (attempts >= 20) window.clearInterval(retryId)
    }, 250)

    return () => window.clearInterval(retryId)
  }, [formId, formType, prefillContext])

  useEffect(() => {
    if (containerRef.current) {
      runThirdParty()
    }
  }, [formId])

  if (!formId || !formApiUrl) return null

  if (outcome) {
    return (
      <div className="px-4 sm:px-8 lg:container lg:mx-auto lg:px-12 mb-8 lg:mb-12">
        <div className="registration-form-outcome" role="status" aria-live="polite">
          {outcome.html ? (
            <div dangerouslySetInnerHTML={{ __html: outcome.html }} />
          ) : (
            <p>{outcome.message}</p>
          )}
          {outcome.redirectUrl && (
            <a href={outcome.redirectUrl} className="btn btn-primary">
              {ctaLabel(outcome.status)}
            </a>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-8 lg:container lg:mx-auto lg:px-12 mb-8 lg:mb-12">
      <div className={`registration-form-embed relative ${submitting ? 'min-h-[600px]' : ''}`} aria-busy={submitting}>
        {/* On submit, hide the embed and show the ~600px spinner cover in its place so the loading
            spinner is clearly visible (never clipped by the embed) until the redirect completes.
            display:none keeps the DOM intact, so any post-submit reads of the form still work. */}
        <div
          ref={containerRef}
          key={`registration-form-${formId}`}
          className={submitting ? 'hidden' : undefined}
          data-form-id={formId}
          data-form-api-url={formApiUrl}
          data-cached-form-url={cachedFormUrl}
          {...prefillDataAttributes(prefill)}
        />
        {submitting ? <FormSubmitOverlay className="rounded-3xl rounded-tl-none" /> : null}
      </div>
    </div>
  )
}

function readSubmittedEmail(formRoot) {
  if (!formRoot?.querySelector) return null
  const names = ['emailaddress1', 'emailaddress2', 'emailaddress3', 'email']
  for (const name of names) {
    const field = formRoot.querySelector(`[name="${name}"]`)
    const value = field?.value?.trim()
    if (value) return value
  }
  return null
}

function isSubmissionSuccessful(event) {
  const detail = event?.detail ?? {}
  if (detail.isSuccess === false) return false
  if (typeof detail.submissionStatus === 'number' && detail.submissionStatus < 0) return false
  return true
}

/** Sent as the contact id when no D365 contact is known — the server reads it as "new applicant". */
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000'

/**
 * Query-string names accepted for each prefill value. Links built by D365 marketing emails use the
 * D365 field names (firstname / lastname / emailaddress1); our own links use the short form
 * (fname / lname / email). Both are read so a link written either way pre-fills the form.
 */
const PREFILL_PARAM_NAMES = {
  contactId: ['contactid'],
  providerId: ['providerid', 'accountid'],
  firstName: ['fname', 'firstname'],
  lastName: ['lname', 'lastname'],
  email: ['email', 'emailaddress1'],
  title: ['title', 'mcs_title'],
  mobile: ['mobile', 'mobilephone'],
  organisationName: ['orgname', 'organisationname', 'organizationname', 'adx_organizationname'],
  positionTitle: ['position', 'jobtitle'],
  state: ['state', 'address1_stateorprovince'],
}

/**
 * Drops null / empty members so a field D365 doesn't know about can't blank out one the query string
 * did supply. Returns null when nothing is left to apply.
 */
function nonEmptyValues(source) {
  if (!source) return null
  const result = {}
  for (const [key, value] of Object.entries(source)) {
    if (value !== null && value !== undefined && value !== '') result[key] = value
  }
  return Object.keys(result).length > 0 ? result : null
}

/** Reads a query-string value by any of its accepted names, case-insensitively. */
function pickParam(prefill, names) {
  if (!prefill) return undefined
  const lookup = {}
  for (const [key, value] of Object.entries(prefill)) {
    lookup[key.toLowerCase()] = value
  }
  for (const name of names) {
    const value = lookup[name]
    if (value) return value
  }
  return undefined
}

function parseContactId(queryParams) {
  const raw = pickParam(queryParams, PREFILL_PARAM_NAMES.contactId)
  if (!raw) return EMPTY_GUID
  return raw
}

/**
 * The contact id posted with a form submission.
 *
 * The staff-registration link carries only a signed token — BuildStaffRegistrationPath appends
 * `token` to StaffRegistrationFormUrl and nothing else — so reading the query string alone always
 * yields EMPTY_GUID for that flow. The server reads an empty hint as "brand-new applicant", which
 * stamps a staff member of an existing member org as a General Registrant in D365 and emails NDS
 * "New membership application submitted" for someone who is only activating website access.
 *
 * prefillContext already merges the query string with the values the token resolved server-side
 * (token winning), so prefer it and fall back to the query string when no token was presented.
 */
function resolveContactId(prefillContext, queryParams) {
  return prefillContext?.contactId || parseContactId(queryParams)
}

function buildRegistrationPrefillContext(prefill) {
  if (!prefill) return null
  const context = {}
  for (const [field, names] of Object.entries(PREFILL_PARAM_NAMES)) {
    const value = pickParam(prefill, names)
    if (value) context[field] = value
  }
  return Object.keys(context).length > 0 ? context : null
}

function ctaLabel(status) {
  switch (status) {
    case 'AlreadyHasLogin':
      return 'Go to login'
    case 'OrganisationIsMember':
      return 'Complete staff registration'
    case 'ContactMembershipTeam':
      return 'Contact membership team'
    default:
      return 'Continue'
  }
}

function prefillDataAttributes(prefill) {
  if (!prefill) return {}
  const attrs = {}
  const assign = (field, attr) => {
    const value = pickParam(prefill, PREFILL_PARAM_NAMES[field])
    if (value) attrs[attr] = value
  }
  assign('contactId', 'data-prefill-contact-id')
  assign('providerId', 'data-prefill-provider-id')
  assign('firstName', 'data-prefill-first-name')
  assign('lastName', 'data-prefill-last-name')
  assign('email', 'data-prefill-email')
  return attrs
}

RegistrationForm.propTypes = {
  formApiUrl: PropTypes.string,
  cachedFormBaseUrl: PropTypes.string,
  checkFormId: PropTypes.string,
  providerFormId: PropTypes.string,
  newUserFormId: PropTypes.string,
  allowedOrigin: PropTypes.string,
  callbackUrl: PropTypes.string,
  submitCallbackUrl: PropTypes.string,
  prefillUrl: PropTypes.string,
  successMessage: PropTypes.string,
  redirectOnSuccessUrl: PropTypes.string,
  isSectionVisible: PropTypes.bool,
}

export default RegistrationForm
